import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import './BulkRunReport.scss';

const STAT_CONFIGS = {
  unlink: [
    { label: 'Processed', key: 'processed' },
    { label: 'Pins unlinked', key: 'pinsUpdated' },
    { label: 'Variants unlinked', key: 'variantsUpdated' },
    { label: 'Conflicts skipped', key: 'skippedConflicts' },
    { label: 'Out of scope', key: 'skippedOutOfScope', hideWhenZero: true },
    { label: 'Duplicates removed', key: 'duplicatesRemoved', hideWhenZero: true },
    { label: 'Not found', key: 'notFound', hideWhenZero: true },
    { label: 'Errors', key: 'errors', hideWhenZero: true }
  ],
  link: [
    { label: 'Processed', key: 'processed' },
    { label: 'Linked as variant', key: 'linkedAsVariant' },
    { label: 'Linked as pin', key: 'linkedAsPin' },
    { label: 'Pins linked now', key: 'pinsLinkedNow' },
    { label: 'Pins auto-linking via cascade', key: 'pinsQueuedViaCascade' },
    { label: 'Conflicts skipped', key: 'skippedConflicts', hideWhenZero: true },
    { label: 'Duplicates removed', key: 'duplicatesRemoved', hideWhenZero: true },
    { label: 'Not found', key: 'notFound', hideWhenZero: true },
    { label: 'Errors', key: 'errors', hideWhenZero: true }
  ]
};

/** Itemized post-run report: stat tiles, per-domain rollup, and the per-link detail list. */
const BulkRunReport = props => {
  const { mode, report } = props;

  const stats = STAT_CONFIGS[mode].filter(stat => !(stat.hideWhenZero && !report[stat.key]));

  const successDetails = (report.details || []).filter(detail => detail.status === 'success');
  const domainRollup = _.orderBy(
    Object.entries(_.groupBy(successDetails.filter(detail => detail.domain), 'domain')).map(([domain, rows]) => ({
      domain,
      urls: rows.length,
      pins: _.sumBy(rows, row => row.pinsUpdated || 0),
      variants: _.sumBy(rows, row => row.variantsUpdated || 0)
    })),
    'urls',
    'desc'
  );

  return (
    <div className='catalog-run-report'>
      <h2>Post-run report</h2>

      <div className='report-stats'>
        {stats.map(stat => (
          <div key={stat.key} className='stat'>
            <span className='stat-value'>{(report[stat.key] || 0).toLocaleString()}</span>
            <span className='stat-label'>{stat.label}</span>
          </div>
        ))}
      </div>

      {report.archivedDomains?.length > 0 && (
        <div className='report-archived'>Archived domains: {report.archivedDomains.join(', ')}</div>
      )}

      {domainRollup.length > 0 && (
        <div className='report-rollup'>
          <h3>By domain</h3>
          <ul>
            {domainRollup.map(row => (
              <li key={row.domain}>
                <span className='rollup-domain'>{row.domain}</span>
                <span className='rollup-stats'>
                  {row.urls.toLocaleString()} URLs
                  {mode === 'unlink' ? ` · ${row.pins.toLocaleString()} pins · ${row.variants.toLocaleString()} variants` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.details?.length > 0 && (
        <div className='report-details'>
          <h3>Details</h3>
          <ul>
            {report.details.map((detail, index) => (
              <li key={index} className={cn(detail.status)}>
                <span className='detail-link'>{detail.link}</span>
                <span className='detail-message'>{detail.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

BulkRunReport.propTypes = {
  mode: PropTypes.oneOf(['unlink', 'link']).isRequired,
  report: PropTypes.object.isRequired
};

export default BulkRunReport;
